import { useState, useEffect } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Target, Activity, Zap } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CEI Score Display Component
 * Shows the Capital Efficiency Index score with trend and breakdown
 */
export function CEIScoreDisplay({ variant = "full", className = "" }) {
  const [ceiData, setCeiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCEI();
  }, []);

  const fetchCEI = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/cei/score?include_breakdown=true`,
        { withCredentials: true }
      );
      setCeiData(response.data);
    } catch (error) {
      console.error("Failed to fetch CEI:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <span className="text-[10px] text-white/40">Loading CEI...</span>
      </div>
    );
  }

  if (!ceiData) return null;

  // Building state - not enough deals
  if (ceiData.status === "building") {
    return <CEIBuildingState data={ceiData} variant={variant} className={className} />;
  }

  // Active CEI display
  if (variant === "compact") {
    return <CEICompact data={ceiData} className={className} />;
  }

  if (variant === "header") {
    return <CEIHeader data={ceiData} className={className} />;
  }

  return <CEIFull data={ceiData} className={className} />;
}

// Building state component
function CEIBuildingState({ data, variant, className }) {
  const progress = (data.deals_counted / 5) * 100;
  
  if (variant === "compact" || variant === "header") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Target className="w-4 h-4 text-white/40" />
        <div>
          <span className="text-[10px] text-white/50 uppercase tracking-wider">CEI</span>
          <span className="text-sm text-white/40 ml-2">Building...</span>
        </div>
        <div className="w-16 h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-zinc-600 to-zinc-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-[10px] text-white/40">{data.deals_counted}/5</span>
      </div>
    );
  }

  return (
    <div className={`bg-[#0A0A0A] border border-white/[0.04] rounded-md p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-white/40" />
          <span className="text-sm font-medium text-white">Capital Efficiency Index</span>
        </div>
        <span className="px-2 py-0.5 bg-[#0A0A0A] text-white/50 text-[10px] font-medium rounded">BUILDING</span>
      </div>
      
      <p className="text-sm text-white/50 mb-3">
        Close {data.deals_needed} more deal{data.deals_needed > 1 ? 's' : ''} to unlock your CEI score
      </p>
      
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-[#0A0A0A] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#D4AF37]/50 to-[#D4AF37] rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm font-mono text-white/50">{data.deals_counted}/5</span>
      </div>
    </div>
  );
}

// Compact variant (for headers/strips)
function CEICompact({ data, className }) {
  const isPositive = data.market_comparison >= 0;
  
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex items-center gap-1.5">
        <Zap className="w-4 h-4" style={{ color: data.color }} />
        <span className="text-[10px] text-white/50 uppercase tracking-wider">CEI</span>
      </div>
      <span 
        className="text-xl font-light"
        style={{ color: data.color }}
      >
        {data.cei_score}
      </span>
      {data.trend_30d !== 0 && (
        <span className={`flex items-center gap-0.5 text-[10px] font-mono ${
          data.trend_30d > 0 ? 'text-emerald-400' : 'text-red-400'
        }`}>
          {data.trend_30d > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {data.trend_30d > 0 ? '+' : ''}{data.trend_30d}
        </span>
      )}
    </div>
  );
}

// Header variant (for Command Center header)
function CEIHeader({ data, className }) {
  const isPositive = data.market_comparison >= 0;
  
  return (
    <div className={className}>
      <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">
        Capital Efficiency
      </div>
      <div className="flex items-baseline gap-2">
        <span 
          className="text-xl font-light tracking-tight"
          style={{ color: data.color }}
        >
          {data.cei_score}
        </span>
        {data.market_comparison !== null && (
          <span className={`flex items-center gap-0.5 text-[10px] font-mono ${
            isPositive ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isPositive ? '+' : ''}{data.market_comparison}
          </span>
        )}
      </div>
      <div className="text-[9px] text-white/40 mt-0.5">
        vs. {data.market_median} median
      </div>
    </div>
  );
}

// Full variant (for dedicated display)
function CEIFull({ data, className }) {
  const isPositive = data.market_comparison >= 0;
  const breakdown = data.breakdown || {};
  
  return (
    <div className={`bg-[#0A0A0A] border border-white/[0.04] rounded-md p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-md flex items-center justify-center"
            style={{ background: `${data.color}15`, borderColor: `${data.color}30`, borderWidth: '1px' }}
          >
            <Zap className="w-5 h-5" style={{ color: data.color }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Capital Efficiency Index</h3>
            <p className="text-[10px] text-white/50">{data.deals_counted} closed transactions</p>
          </div>
        </div>
        <div 
          className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: data.color, background: `${data.color}15` }}
        >
          {data.rating}
        </div>
      </div>

      {/* Main Score */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <span 
            className="text-5xl font-light"
            style={{ color: data.color }}
          >
            {data.cei_score}
          </span>
          <span className="text-xl text-white/40 ml-1">/100</span>
        </div>
        
        <div className="text-right">
          <div className={`flex items-center gap-1 justify-end ${
            isPositive ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="text-lg font-mono">
              {isPositive ? '+' : ''}{data.market_comparison}
            </span>
          </div>
          <span className="text-[10px] text-white/40">vs market median ({data.market_median})</span>
        </div>
      </div>

      {/* 30-Day Trend */}
      {data.trend_30d !== null && data.trend_30d !== 0 && (
        <div className="mb-4 p-3 bg-white/[0.02] border border-white/[0.04] rounded-md">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-white/50 uppercase tracking-wider">30-Day Trend</span>
            <span className={`flex items-center gap-1 text-sm font-mono ${
              data.trend_30d > 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {data.trend_30d > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {data.trend_30d > 0 ? '+' : ''}{data.trend_30d}%
            </span>
          </div>
        </div>
      )}

      {/* Breakdown */}
      {breakdown && Object.keys(breakdown).length > 0 && (
        <div className="space-y-3">
          <h4 className="text-[10px] text-white/40 uppercase tracking-wider">Score Breakdown</h4>
          
          <BreakdownBar 
            label="Profit Efficiency" 
            value={breakdown.profit_efficiency} 
            weight={breakdown.weights?.profit_efficiency}
          />
          <BreakdownBar 
            label="Velocity" 
            value={breakdown.velocity} 
            weight={breakdown.weights?.velocity}
          />
          <BreakdownBar 
            label="Risk Discipline" 
            value={breakdown.risk_discipline} 
            weight={breakdown.weights?.risk_discipline}
          />
          <BreakdownBar 
            label="Consistency" 
            value={breakdown.consistency} 
            weight={breakdown.weights?.consistency}
          />
        </div>
      )}
    </div>
  );
}

// Breakdown progress bar
function BreakdownBar({ label, value, weight }) {
  const getColor = (val) => {
    if (val >= 70) return '#10B981';
    if (val >= 50) return '#D4AF37';
    if (val >= 30) return '#F59E0B';
    return '#EF4444';
  };
  
  const color = getColor(value);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-white/70">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/40">{Math.round(weight * 100)}%</span>
          <span className="text-sm font-mono" style={{ color }}>{value}</span>
        </div>
      </div>
      <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
    </div>
  );
}

export default CEIScoreDisplay;
